import DataPropertyProfileConnector from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileConnector";
import TradeContractView from "@/components/pages/contract/TradeContract/TradeContractView";
import {DataPropertyProfileLink} from "@/components/pages/contract/TradeContract/AdaptContract/DataPropertyProfileLink";
import {DataPropertyProfileMap} from "@/store/modules/hcs/tradeContract/model";

/**
 * 自分側と相手側の両方のデータ項目プロファイルID
 */
export type SelfAndOther = {
  selfDataPropertyProfileId: string;
  otherDataPropertyProfileId: string;
};

/**
 * 自分側と相手側の「どちらか」または「両方」のデータ項目プロファイルID
 */
export type SelfOrOther = {
  selfDataPropertyProfileId: null;
  otherDataPropertyProfileId: string;
} | {
  selfDataPropertyProfileId: string;
  otherDataPropertyProfileId: null;
} | SelfAndOther;

/**
 * 提供側と利用側の両方のデータ項目プロファイルID
 */
export type ProducerAndConsumer = {
  producerDataPropertyProfileId: string;
  consumerDataPropertyProfileId: string;
};

/**
 * 提供側と利用側の「どちらか」または「両方」のデータ項目プロファイルID
 */
export type ProducerOrConsumer = {
  producerDataPropertyProfileId: null;
  consumerDataPropertyProfileId: string;
} | {
  producerDataPropertyProfileId: string;
  consumerDataPropertyProfileId: null;
} | ProducerAndConsumer;

/**
 * すべてのデータ項目プロファイルリンクとコネクタを管理する
 */
export default class DataPropertyProfileLinkManager {
  /** TradeContractView を取得する function */
  private readonly tcvProducer: () => TradeContractView;

  /** すべてのデータ項目プロファイルコネクタ */
  private readonly connectors: Map<string, DataPropertyProfileConnector>;

  /** すべてのデータ項目プロファイルリンク */
  private maps: Array<DataPropertyProfileLink>

  /**
   * コンストラクタ
   *
   * @param tcvProducer TradeContractView を取得する function
   */
  constructor(tcvProducer: () => TradeContractView) {
    this.tcvProducer = tcvProducer;
    this.maps = [];
    this.connectors = new Map();
  }

  /**
   * データ項目プロファイルコネクタを登録します
   *
   * @param connector 登録するデータ項目プロファイルコネクタ
   */
  registerConnector(connector: DataPropertyProfileConnector) {
    this.connectors.set(connector.dataPropertyProfileId, connector);
  }

  /**
   * データ項目プロファイルコネクタの登録を解除します
   *
   * @param connector 登録の解除を行うデータ項目プロファイルコネクタ
   */
  unregisterConnector(connector: DataPropertyProfileConnector) {
    this.connectors.delete(connector.dataPropertyProfileId);
  }

  /**
   * 指定されたデータ項目プロファイルIDのコネクタを取得します
   *
   * @param dataPropertyProfileId コネクタを表すデータ項目プロファイルID
   */
  getConnector(dataPropertyProfileId: string) {
    return this.connectors.get(dataPropertyProfileId);
  }

  /**
   * データ項目プロファイルIDのコネクタをすべて取得します
   */
  getConnectors() {
    return Array.from(this.connectors.values());
  }

  /**
   * 指定された条件に一致するデータ項目プロファイルリンクをすべて取得します
   *
   * @param key 検索条件
   */
  get(key: Partial<ProducerOrConsumer> | Partial<SelfOrOther>): DataPropertyProfileLink[] {
    return this.maps.filter(this.buildFilter(key));
  }

  /**
   * 指定された条件に一致するデータ項目プロファイルリンクを取得します
   *
   * @param key 検索条件
   */
  getOne(key: ProducerOrConsumer | SelfOrOther): DataPropertyProfileLink | undefined {
    return this.maps.find(this.buildFilter(key));
  }

  /**
   * 指定されたデータ項目プロファイルリンクを追加します
   *
   * @param key データ項目プロファイルリンク
   */
  add(key: ProducerAndConsumer | SelfAndOther) {
    const exists = this.getOne(key);
    if(exists) {
      return exists;
    }
    const link = new DataPropertyProfileLink(this.tcvProducer, this, key);
    this.maps.push(link);
    return link;
  }

  /**
   * すべてのデータ項目プロファイルリンクを削除します
   */
  clear() {
    this.maps = [];
  }

  /**
   * 指定されたデータ項目プロファイルリンクを削除します
   *
   * @param key 削除条件
   */
  remove(key: ProducerAndConsumer | SelfAndOther) {
    const filter = this.buildFilter(key);
    this.maps = this.maps.filter(map => !filter(map));
  }

  /**
   * 指定されたデータ項目プロファイルリンクをすべて削除します
   *
   * @param key 削除条件
   */
  removeBy(key: Partial<ProducerAndConsumer> | Partial<SelfAndOther>) {
    const filter = this.buildFilter(key);
    this.maps = this.maps.filter(map => !filter(map));
  }

  /**
   * データ項目プロファイルリンクが存在するかどうかを取得します
   */
  get hasLink() {
    return this.maps.length > 0;
  }

  /**
   * データ項目変換マップを取得します
   */
  get dataPropertyProfileMaps(): DataPropertyProfileMap[] {
    const result = this.maps.map(dppm => dppm.toObject());
    for(const conn of this.connectors.values()) {
      if(this.tradeContractView.isSelfConsume && conn.serviceType === 'consume' && !result.find(dppm => dppm.consumerDataPropertyProfileId === conn.dataPropertyProfileId)) {
        result.push({
          producerDataPropertyProfileId: null,
          consumerDataPropertyProfileId: conn.dataPropertyProfileId,
          defaultValue: conn.defaultValue ?? undefined
        });
      }
      if(this.tradeContractView.isSelfProduce && conn.serviceType === 'produce' && !result.find(dppm => dppm.producerDataPropertyProfileId === conn.dataPropertyProfileId)) {
        result.push({
          producerDataPropertyProfileId: conn.dataPropertyProfileId,
          consumerDataPropertyProfileId: null,
          defaultValue: conn.defaultValue ?? undefined
        });
      }
    }
    return result;
  }

  /**
   * データ項目変換マップを設定します
   */
  set dataPropertyProfileMaps(dataPropertyProfileMaps) {
    this.clear();
    for(const dppm of dataPropertyProfileMaps ?? []) {
      // 提供側と利用側と両方指定されている場合
      if(dppm.consumerDataPropertyProfileId && dppm.producerDataPropertyProfileId) {
        const link = this.add({
          producerDataPropertyProfileId: dppm.producerDataPropertyProfileId,
          consumerDataPropertyProfileId: dppm.consumerDataPropertyProfileId
        });
        link.typeConversionMethod = dppm.typeConversionMethod ?? 'auto';
      }

      if(this.tradeContractView.isSelfConsume && dppm.consumerDataPropertyProfileId) {
        const connector = this.getConnector(dppm.consumerDataPropertyProfileId);
        if(connector) {
          connector.defaultValue = dppm.defaultValue ?? null;
        }
      }

      if(this.tradeContractView.isSelfProduce && dppm.producerDataPropertyProfileId) {
        const connector = this.getConnector(dppm.producerDataPropertyProfileId);
        if(connector) {
          connector.defaultValue = dppm.defaultValue ?? null;
        }
      }
    }
  }

  /**
   * TradeContractView を取得します
   * @private
   */
  private get tradeContractView() {
    return this.tcvProducer();
  }

  /**
   * 指定された条件のフィルタを生成します
   *
   * @param key 条件
   * @private
   */
  private buildFilter(key: Partial<ProducerOrConsumer> | Partial<SelfOrOther>): (link: DataPropertyProfileLink) => boolean {
    if('selfDataPropertyProfileId' in key || 'otherDataPropertyProfileId' in key) {
      if(this.tradeContractView.isSelfConsume) {
        return dppLink =>
            (key.selfDataPropertyProfileId === undefined || dppLink.consumerDataPropertyProfileId === key.selfDataPropertyProfileId) &&
            (key.otherDataPropertyProfileId === undefined || dppLink.producerDataPropertyProfileId === key.otherDataPropertyProfileId);
      } else {
        return dppLink =>
            (key.selfDataPropertyProfileId === undefined || dppLink.producerDataPropertyProfileId === key.selfDataPropertyProfileId) &&
            (key.otherDataPropertyProfileId === undefined || dppLink.consumerDataPropertyProfileId === key.otherDataPropertyProfileId);
      }
    } else if('producerDataPropertyProfileId' in key || 'consumerDataPropertyProfileId' in key) {
      return dppLink =>
          (key.producerDataPropertyProfileId === undefined || dppLink.producerDataPropertyProfileId === key.producerDataPropertyProfileId) &&
          (key.consumerDataPropertyProfileId === undefined || dppLink.consumerDataPropertyProfileId === key.consumerDataPropertyProfileId);
    } else {
      return _ => true;
    }
  }
}
